class PredictiveSearchOptimized extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search-optimized]');
    this.resultsWrapper = null;
    this.termsWrapper = null;
    this.dynamicView = null;
    this.trendingView = null;
    this.resultsCountEl = null;
    this.excludedHandles = JSON.parse(this.dataset.exclude || '[]');
    this.onKeydownEscape = this.onKeydownEscape.bind(this);
    this.setup();
  }

  setup() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', e => {
      if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) e.preventDefault();
    });
    this.input.addEventListener('input', () => this.onChange());
    this.input.addEventListener('focus', () => {
      if (this.getQuery().length && this.getAttribute('results') === 'true') this.open();
    });
    this.addEventListener('click', e => {
      const btn = e.target.closest('[data-search-action]');
      if (btn && btn.dataset.searchAction === 'close') this.close();
    });
  }

  connectedCallback() {
    this.resultsWrapper = this.querySelector('[data-results-wrapper]');
    this.termsWrapper = this.querySelector('[data-terms-list]');
    this.dynamicView = this.querySelector('[data-dynamic-view]');
    this.trendingView = this.querySelector('[data-trending-wrapper]');
    this.resultsCountEl = this.querySelector('.results-count[aria-live="assertive"]');
    this.ensureGuards();
  }

  ensureGuards() {
    if (!this.startGuard) {
      this.startGuard = document.createElement('div');
      this.startGuard.setAttribute('tabindex', '0');
      this.startGuard.setAttribute('data-focus-guard', 'start');
      this.startGuard.style.cssText = 'position:fixed;width:1px;height:1px;opacity:0;pointer-events:none;';
      this.startGuard.addEventListener('focus', () => {
        const els = this.focusables();
        if (els.length) els[els.length - 1].focus();
      });
      this.insertBefore(this.startGuard, this.firstChild);
    }
    if (!this.endGuard) {
      this.endGuard = document.createElement('div');
      this.endGuard.setAttribute('tabindex', '0');
      this.endGuard.setAttribute('data-focus-guard', 'end');
      this.endGuard.style.cssText = 'position:fixed;width:1px;height:1px;opacity:0;pointer-events:none;';
      this.endGuard.addEventListener('focus', () => {
        const els = this.focusables();
        if (els.length) els[0].focus();
      });
      this.appendChild(this.endGuard);
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const q = this.getQuery();
    if (!q) {
      this.updateResultsCount('');
      this.close(true);
      return;
    }
    this.getSearchResults(q);
  }

  async getSearchResults(q) {
    try {
      const r = await fetch(`${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(q)}&resources[limit]=5&resources[type]=product&resources[options][unavailable_products]=show&resources[options][fields]=title,product_type,tag,variants.title,variants.sku`);
      const s = await r.json();
      const products = s?.resources?.results?.products || [];
      const list = products.filter(({ tags = [], handle }) =>
        !this.excludedHandles.includes(handle) &&
        !tags.includes('base_product') &&
        !tags.includes('hide_product') &&
        !tags.includes('locked')
      );

      if (!list.length) {
        this.dynamicView?.classList.add('sp-hidden');
        this.trendingView?.classList.remove('sp-hidden');
        this.resultsWrapper.innerHTML = '';
        this.termsWrapper.innerHTML = '';
        this.setAttribute('results', 'true');
        this.updateResultsCount(`0 results for "${q}". Showing trending items.`);
        this.open();
        return;
      }

      this.dynamicView?.classList.remove('sp-hidden');
      this.trendingView?.classList.add('sp-hidden');

      const terms = list.map(p => p.title).filter(t => t.length > 1);
      this.termsWrapper.innerHTML = terms.map((t, i) => `<li role="option" id="term-${i}"><a href="/search?q=${encodeURIComponent(t)}">${t}</a></li>`).join('');
      this.resultsWrapper.innerHTML = list.slice(0, 4).map((p, i) => `
        <li role="option" id="result-${i}"><intersection-loader data-url="/products/${p.handle}" data-section-id="product-tile-for-search"></intersection-loader></li>
      `).join('');

      this.setAttribute('results', 'true');
      this.updateResultsCount(`${list.length} ${list.length === 1 ? 'result' : 'results'} for "${q}"`);
      this.open();
    } catch (err) {
      this.dynamicView?.classList.add('sp-hidden');
      this.trendingView?.classList.remove('sp-hidden');
      this.resultsWrapper.innerHTML = '';
      this.termsWrapper.innerHTML = '';
      this.setAttribute('results', 'false');
      this.updateResultsCount('Search is temporarily unavailable. Please try again.');
    }
  }

  open() {
    this.setAttribute('open', '');
    this.input.setAttribute('aria-expanded', 'true');
    this.ensureGuards();
    document.addEventListener('keydown', this.onKeydownEscape);
    if (!this.contains(document.activeElement)) this.input.focus();
  }

  close(clear = false) {
    if (clear) {
      this.input.value = '';
      this.removeAttribute('results');
      this.updateResultsCount('');
    }
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', this.onKeydownEscape);
  }

  onKeydownEscape(e) {
    if (!this.hasAttribute('open')) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      this.close();
      this.input.focus();
    }
  }

  focusables() {
    return Array.from(this.querySelectorAll(
      'a[href]:not([disabled]):not([tabindex="-1"]),button:not([disabled]):not([tabindex="-1"]),' +
      'input:not([disabled]):not([tabindex="-1"]),select:not([disabled]):not([tabindex="-1"]),' +
      'textarea:not([disabled]):not([tabindex="-1"]),[tabindex]:not([tabindex="-1"]):not([data-focus-guard])'
    )).filter(el => {
      const s = getComputedStyle(el);
      return s.display !== 'none' && s.visibility !== 'hidden';
    });
  }

  updateResultsCount(text) {
    if (!this.resultsCountEl) return;
    this.resultsCountEl.textContent = text || '';
  }
}

customElements.define('predictive-search-optimized', PredictiveSearchOptimized);
